import React, { useEffect, useState } from "react";
import { LeaveAttendanceApi } from "../../../services/LeaveAttendance";
import { toast } from "react-toastify";
import { LeaveService } from "../../../services/leave";

interface LeaveProps {
  setApplyOpenLeave: (data: any) => void;
  setBalancedLeave: (data: any) => void;
  setleaveRequestData: (data: any) => void;
}

interface LeaveData {
  leaveType: string;
  startDate: string;
  endDate: string;
  reason: string;
  halfDayType: string;
  forwardApprover: number | null | string;
}

const ApplyLeave = (props: LeaveProps) => {
  const initialLeaveData: LeaveData = {
    leaveType: '',
    startDate: '',
    endDate: '',
    reason: '',
    halfDayType: '',
    forwardApprover: null,
  };
  const [appliedLeaveData, setappliedLeaveData] = useState<LeaveData>(initialLeaveData);

  const { applyForLeave, getAddLeave } = LeaveAttendanceApi();
  const { getStepHierarchyByDesignation } = LeaveService();

  const [validationErrors, setValidationErrors] = useState<Partial<LeaveData>>({});
  const [leave, setleaves] = useState<any>([]);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [forwardUsers, setForwardUsers] = useState<any[]>([]);

  const currentDate = new Date().toISOString().split('T')[0];

  useEffect(() => {
    getData();
    getFirstStepForward();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch leave data
  const getData = async () => {
    const response = await getAddLeave();
    setleaves(response.data.data);
  };

  // Fetch forward approvers (step 1 users)
  const getFirstStepForward = async () => {
    try {
      const response = await getStepHierarchyByDesignation();
      setForwardUsers(response.data.data || []);
    } catch (error) {
      console.error("Failed to fetch forward approvers", error);
    }
  };

  // Handle generic input & textarea change
  const handleInputChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = event.target;
    let errors = { ...validationErrors };

    if (name === 'startDate' || name === 'endDate') {
      const currentDate = new Date().toISOString().split('T')[0];
      if (value < currentDate) {
        errors[name] = 'Date cannot be before the current date';
      } else {
        errors[name] = '';
      }

      if (
        name === 'startDate' &&
        appliedLeaveData.endDate !== '' &&
        value > appliedLeaveData.endDate
      ) {
        errors['startDate'] = 'Start date cannot be after end date';
      } else if (
        name === 'endDate' &&
        appliedLeaveData.startDate !== '' &&
        value < appliedLeaveData.startDate
      ) {
        errors['endDate'] = 'End date cannot be before start date';
      }
    }

    setValidationErrors(errors);

    if (name === "startDate" && appliedLeaveData.leaveType === "Half Day Leave") {
      // For half day leave, endDate should always be startDate
      setappliedLeaveData({
        ...appliedLeaveData,
        startDate: value,
        endDate: value,
      });
    } else {
      // For other cases, just update the changed field normally
      setappliedLeaveData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };


  // Handle Leave Type Change
  const handleLeaveTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.target;
    const newEndDate =
      value === "Half Day Leave" ? appliedLeaveData.startDate : appliedLeaveData.endDate;
    setappliedLeaveData({
      ...appliedLeaveData,
      [name]: value,
      endDate: newEndDate,
    });
  };

  // Handle Half Day Type change
  const handleHalfDayTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.target;
    const newEndDate =
      value === "Morning session" || value === "Afternoon session" ? appliedLeaveData.startDate : "";
    setappliedLeaveData({
      ...appliedLeaveData,
      [name]: value,
      endDate: newEndDate,
    });
  };

  // Validate form before submission
  const validateForm = (): boolean => {
    const errors: Partial<LeaveData> = {};

    if (appliedLeaveData.leaveType === '') {
      errors.leaveType = 'Leave Type is required';
    }
    if (appliedLeaveData.startDate === '') {
      errors.startDate = 'Start date is required';
    }
    if (appliedLeaveData.endDate === '') {
      errors.endDate = 'End date is required';
    }
    if (appliedLeaveData.reason === '') {
      errors.reason = 'Reason is required';
    }
    if (appliedLeaveData.leaveType === 'Half Day Leave' && appliedLeaveData.halfDayType === '') {
      errors.halfDayType = 'Please select a half day type';
    }
    if (appliedLeaveData.forwardApprover === null) {
      errors.forwardApprover = 'Please select a forward approver';
    }

    setValidationErrors(errors);

    return Object.keys(errors).length === 0;
  };

  // Handle form submit
  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!validateForm()) {
      toast.error("Kindly check Data before Submit");
      return;
    }

    try {
      // Additional validations:
      if (appliedLeaveData.startDate > appliedLeaveData.endDate) {
        setValidationErrors((prev) => ({
          ...prev,
          startDate: 'Start date cannot be after end date',
          endDate: 'End date cannot be before start date',
        }));
        toast.error("Kindly check dates before Submit");
        return;
      }

      const currentDateFor = new Date();
      const currentHour = currentDateFor.getHours();
      const today = new Date().toISOString().split('T')[0];

      if (
        currentHour >= 8 &&
        !appliedLeaveData.leaveType.includes("Half") &&
        appliedLeaveData.startDate === today
      ) {
        toast.error("Invalid leave request: Only half day leave is allowed after 08:00 AM.");
        return;
      }

      // Prepare FormData
      const formData = new FormData();
      Object.entries(appliedLeaveData).forEach(([key, value]) => {
        // forwardApprover is number, convert to string
        formData.append(key, value === null ? '' : String(value));
      });

      if (selectedFile) {
        formData.append('file', selectedFile);
      }

      const response = await applyForLeave(formData);

      response?.data.data.forEach((message: any) => {
        if (message.status === 400) {
          toast.error(message.message);
        } else {
          toast.success(message.message);
        }
      });

      setTimeout(() => {
        props.setApplyOpenLeave(false);
      }, 100);

      props.setBalancedLeave(response?.status);
      props.setleaveRequestData(response?.data.data);
      setappliedLeaveData(initialLeaveData);
      setValidationErrors({});
      setSelectedFile(null);

    } catch (error) {
      console.error('Error:', error);
      toast.error("An error occurred while submitting leave.");
    }
  };

  return (
    <div className="modal-content py-5 px-6 bg-white rounded-lg max-w-3xl mx-auto sm:px-4 md:px-6 lg:px-8">
      <form onSubmit={handleSubmit}>
        <h2 className="text-xl font-bold text-center py-4 px-6 text-black">Leave Management Form</h2>

        {/* Leave Type */}
        <div className="mb-6">
          <label htmlFor="leaveType" className="block mb-2 text-xl font-medium text-black dark:text-white"><b>Leave Type:</b></label>
          <select
            id="leaveType"
            name="leaveType"
            value={appliedLeaveData.leaveType}
            onChange={handleLeaveTypeChange}
            className="bg-gray-50 border border-gray-300 px-4 py-2 text-sm rounded-lg block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:ring-blue-500 focus:border-blue-500 text-black"
          >
            <option value="" disabled>Select Leave Type</option>
            {leave.filter((l: any) => l.status).map((leaveItem: any) => (
              <option key={leaveItem.id} value={leaveItem.leaveType}>{leaveItem.leaveType}</option>
            ))}
          </select>
          {validationErrors.leaveType && <span className="error">{validationErrors.leaveType}</span>}
        </div>

        {/* Dates side by side on md */}
        <div className="md:flex md:space-x-4">
          <div className="md:w-1/2 mb-6">
            <label htmlFor="startDate" className="block mb-2 text-xl font-medium text-black dark:text-white"><b>Start Date:</b></label>
            <input
              type="date"
              id="startDate"
              name="startDate"
              value={appliedLeaveData.startDate}
              onChange={handleInputChange}
              min={currentDate}
              className="bg-gray-50 border border-gray-300 text-gray-800 text-sm rounded-lg block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:ring-blue-500 focus:border-blue-500"
            />
            {validationErrors.startDate && <span className="error">{validationErrors.startDate}</span>}
          </div>

          <div className="md:w-1/2 mb-6">
            {(appliedLeaveData.leaveType === 'Half Day Leave' || appliedLeaveData.leaveType.includes('Half')) ? (
              <>
                <label htmlFor="halfDayType" className="block mb-2 text-xl font-medium text-black dark:text-white"><b>Half Day Type:</b></label>
                <select
                  id="halfDayType"
                  name="halfDayType"
                  value={appliedLeaveData.halfDayType}
                  onChange={handleHalfDayTypeChange}
                  className="bg-gray-50 border border-gray-300 px-4 py-2 text-sm rounded-lg block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="" disabled>Select Value</option>
                  <option value="Morning session">Morning session</option>
                  <option value="Afternoon session">Afternoon session</option>
                </select>
                {validationErrors.halfDayType && <span className="error">{validationErrors.halfDayType}</span>}
              </>
            ) : (
              <>
                <label htmlFor="endDate" className="block mb-2 text-xl font-medium text-black dark:text-white"><b>End Date:</b></label>
                <input
                  type="date"
                  id="endDate"
                  name="endDate"
                  value={appliedLeaveData.endDate}
                  onChange={handleInputChange}
                  min={currentDate}
                  className="bg-gray-50 border border-gray-300 text-gray-800 text-sm rounded-lg block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:ring-blue-500 focus:border-blue-500"
                />
                {validationErrors.endDate && <span className="error">{validationErrors.endDate}</span>}
              </>
            )}
          </div>
        </div>

        {/* Reason */}
        <div className="mb-6">
          <label htmlFor="reason" className="block mb-2 text-xl font-medium text-black dark:text-white"><b>Reason:</b></label>
          <textarea
            id="reason"
            name="reason"
            value={appliedLeaveData.reason}
            onChange={handleInputChange}
            className="block p-2.5 w-full text-sm bg-gray-50 rounded-lg border border-gray-300 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
            rows={4}
          />
          {validationErrors.reason && <span className="error">{validationErrors.reason}</span>}
        </div>

        {/* Upload document */}
        {/* <div className="mb-6">
          <label htmlFor="documentUpload" className="block mb-2 text-xl font-medium text-black dark:text-white"><b>Upload Document:</b></label>
          <input
            type="file"
            id="documentUpload"
            name="documentUpload"
            accept=".pdf,image/*"
            onChange={e => setSelectedFile(e.target.files ? e.target.files[0] : null)}
            className="block w-full text-sm text-gray-900 border border-gray-300 rounded-lg cursor-pointer bg-gray-50 dark:text-gray-400 focus:outline-none dark:bg-gray-700 dark:border-gray-600"
          />
          <span className="text-xs text-gray-500">Upload supporting document (e.g., medical certificate) if required.</span>
          {selectedFile && <span className="text-green-600 text-sm">Selected: {selectedFile.name}</span>}
        </div> */}

        {/* Forward to Approver dropdown */}
        <div className="mb-6">
          <label htmlFor="forwardApprover" className="block mb-2 text-xl font-medium text-black dark:text-white"><b>Forward to Approver:</b></label>
          <select
            id="forwardApprover"
            name="forwardApprover"
            value={appliedLeaveData.forwardApprover ?? ''}
            onChange={e => {
              const val = Number(e.target.value);
              setappliedLeaveData({
                ...appliedLeaveData,
                forwardApprover: isNaN(val) ? null : val,
              });
            }}
            className="bg-gray-50 border border-gray-300 px-4 py-2 text-sm rounded-lg block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="" disabled>
              Select Approver
            </option>
            {forwardUsers.map((user) => (
              <option key={user.id} value={user.id}>
                {user.firstName} {user.lastName} ({user.organizationUnit})
              </option>
            ))}
          </select>
          {validationErrors.forwardApprover && (
            <span className="error">{validationErrors.forwardApprover}</span>
          )}
        </div>

        {/* Buttons */}
        <div className="flex flex-col sm:flex-row sm:justify-center sm:space-x-6 mt-4 space-y-3 sm:space-y-0">
          <button
            type="submit"
            className="text-white bg-blue-700 hover:bg-blue-800 focus:ring-4 focus:ring-blue-300 font-medium rounded-lg text-sm px-5 py-2.5 dark:bg-blue-600 dark:hover:bg-blue-700 focus:outline-none dark:focus:ring-blue-800"
          >
            Submit
          </button>
          <button
            type="button"
            onClick={() => props.setApplyOpenLeave(false)}
            className="text-white bg-red-700 hover:bg-red-800 focus:ring-4 focus:ring-red-300 font-medium rounded-lg text-sm px-5 py-2.5 dark:bg-red-600 dark:hover:bg-red-700 focus:outline-none dark:focus:ring-red-900"
          >
            Close
          </button>
        </div>
      </form>
    </div>
  );
};

export default ApplyLeave;
